import React, { useEffect, useRef, useState } from 'react';
import { Button, Popover, message } from 'antd';
import InputNumber from '../../../forms/InputNumber';
import { UPDATE_PUBLICATION_QUERY } from '../../../../utils/api-utils';
import { useMutation } from '@apollo/client';
import { LoadingOutlined, SaveOutlined } from '@ant-design/icons';
import { CampaignType } from '../../../../__generated__/graphql';
import { useCurrentUser } from '../../../../utils/CurrentUserContext';
import { getWebsiteFloorPrice } from '../../../forms/Campaign/WebsitesForm';

interface RateCellProps {
  defaultBid: number;
  website: any;
  campaignType: CampaignType;
  multiplier?: number;
  publicationId: string;
}

const RateCell: React.FC<RateCellProps> = ({ defaultBid, website, campaignType, multiplier = 1, publicationId }) => {
  const [currentMultiplier, setCurrentMultiplier] = useState<number>(multiplier);
  const [savedMultiplier, setSavedMultiplier] = useState<number>(multiplier);

  // Re-seed local state when the row is reused for a different publication
  // (e.g. when paginating) or when the saved multiplier changes server-side.
  // The component instance is not always remounted, so the initial useState
  // value alone would keep stale values from a previous page.
  useEffect(() => {
    if (saving) return;
    setCurrentMultiplier(multiplier);
    setSavedMultiplier(multiplier);
  }, [publicationId, multiplier]);

  const [updatePublication] = useMutation(
    UPDATE_PUBLICATION_QUERY, {
      refetchQueries: ['PublicationMany']
    }
  );
  const {user} = useCurrentUser()

  // Keep the spinner visible for a short moment after the request resolves
  // so the loading state is perceptible even on very fast responses.
  const [saving, setSaving] = useState(false);
  const spinnerTimeout = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(spinnerTimeout.current), []);

  const notDirty = currentMultiplier === savedMultiplier;

  const handleMultiplierChange = (value: number | null) => {
    setCurrentMultiplier(value ?? 0.1);
  };

  const handleSave = async () => {
    if (notDirty || saving) return;

    setSaving(true);
    try {
      const {data} = await updatePublication({
        variables: {
          id: publicationId,
          record: {rateCard: {bidMultiplier: currentMultiplier}},
        },
      })

      const returnedMultiplier =
        data?.PublicationUpdateById?.rateCard?.bidMultiplier

      if (Number(returnedMultiplier) !== currentMultiplier) {
        message.error('Bid multiplier not saved')
        return
      }

      setSavedMultiplier(currentMultiplier);
    } catch {
      message.error('Bid multiplier not saved')
    } finally {
      // Let the spinner linger briefly after the response.
      spinnerTimeout.current = setTimeout(() => setSaving(false), 500)
    }
  }
  const floorPrice = getWebsiteFloorPrice(user, website, campaignType)

  return (
    <>
      <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
        <InputNumber
          className="w-24"
          min={floorPrice ? Math.ceil(floorPrice * 100 / defaultBid) / 100 : 0.1}
          step={0.1}
          onBlur={handleSave}
          precision={2}
          value={currentMultiplier}
          onChange={handleMultiplierChange}
        />
        <span className="ml-2">× {defaultBid} =</span>
        <Popover content={floorPrice && <div className='text-xs'>Minimum bid: <strong>{floorPrice}</strong>DKK</div>}>
          <span className="ml-2">{(currentMultiplier * defaultBid).toFixed(2)}DKK</span>
        </Popover>
        <Button icon={saving ? <LoadingOutlined/> : <SaveOutlined/>} className="ml-2" onClick={handleSave} disabled={notDirty || saving} />
      </div>
      {floorPrice && floorPrice > defaultBid && (
        <div className="text-red-500 text-xs">
          Minimum bid: <strong>{floorPrice}</strong>DKK
        </div>
      )}
    </>
  );
};

export default RateCell;
